import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'appkit-react';
import { Modal, Checkbox } from 'antd';
import { EditableCell } from './editable';
import alert from '@components/alert';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { SEGMENTS, ParentAndChildItemMap } from '@model';
// import '@style/modules/analysis/fieldsSetting.scss';
import '../../../styles/modules/analysis/fieldsSetting.scss';
import { API } from '@service';
import { isUuid } from '@utils/common';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove as dndArrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const DragHandle = ({ attributes, listeners }) => (
  <div
    className="drag-handle"
    {...attributes}
    {...listeners}
    style={{ cursor: 'grab' }}
  >
    <span className="appkiticon icon-menu-outline" />
  </div>
);

const checkFieldsDuplicated = (fields, setIsSaveDisabled = () => {}) => {
  const result = [...fields];
  let hasDuplicatedError = false;

  result.map((f) => {
    f.isDuplicatedError = false;
    if (f.isRequiredError) {
      hasDuplicatedError = true;
    }
    return f;
  });
  const mapOfNameAndIndex = new Map();
  fields.forEach((f, i) => {
    if (f.name && mapOfNameAndIndex.get(f.name)) {
      mapOfNameAndIndex.set(f.name, [...mapOfNameAndIndex.get(f.name), i]);
    }

    if (f.name && !mapOfNameAndIndex.get(f.name)) {
      mapOfNameAndIndex.set(f.name, [i]);
    }
  });

  mapOfNameAndIndex.forEach((v, k) => {
    if (v.length > 1) {
      v.forEach((i) => {
        result[i].isDuplicatedError = true;
        hasDuplicatedError = true;
      });
    }
  });

  setIsSaveDisabled(hasDuplicatedError);

  return result;
};

const fieldNameComponent = (field, fields, setFields, setIsSaveDisabled) => {
  if (isUuid(field.id)) {
    return (
      <>
        <EditableCell
          value={field.name ? field.name.replace(/_/g, '/') : ''}
          defaultValue={field.name ? field.name.replace(/_/g, '/') : ''}
          isEditing={true}
          style={
            field.isRequiredError || field.isDuplicatedError
              ? { borderColor: 'var(--activeColor)' }
              : {}
          }
          onInput={(e) => {
            const targetIndex = fields.findIndex((f) => f.id === field.id);
            let result = [...fields];

            const isBlank = !e.target.value;
            result[targetIndex].name = e.target.value;
            result[targetIndex].isRequiredError = isBlank;

            result = checkFieldsDuplicated(result);

            setFields(result);
            setIsSaveDisabled(field.isRequiredError || field.isDuplicatedError);
          }}
          maxLength={40}
        />
        {field.isRequiredError ? (
          <span className="error-text">Field is required!</span>
        ) : null}
        {field.isDuplicatedError ? (
          <span className="error-text">Duplicate field name(s)</span>
        ) : null}
      </>
    );
  }
  return (
    <>
      <span
        style={field.isDuplicatedError ? { color: 'var(--activeColor)' } : {}}
      >
        {field.name ? field.name.replace(/_/g, '/') : ''}
      </span>
    </>
  );
};

const SortableItem = ({
  field,
  onCheckboxChange,
  fields,
  setFields,
  setIsSaveDisabled,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const deleteConfirm = (config = {}) => {
    alert.confirm({
      ...config,
    });
  };

  const handleDelete = (close) => {
    setFields((fields) => {
      const targetIndex = fields.findIndex((f) => f.id === field.id);
      let result = [...fields];
      result.splice(targetIndex, 1);
      result = checkFieldsDuplicated(result, setIsSaveDisabled);
      return result;
    });
    close();
  };

  const confirmDelete = (e) => {
    deleteConfirm({
      onOk: handleDelete,
      content: 'Do you want to delete this field',
    });
  };

  return (
    <div ref={setNodeRef} style={style} className="one-line-warpper ">
      <div className="checkbox-warpper">
        <Checkbox
          checked={!field.isHidden}
          onChange={onCheckboxChange(field.name)}
        />
      </div>
      <div className="left-part-warpper">
        <div className="upper-block">
          <DragHandle attributes={attributes} listeners={listeners} />
          <div className="input-warpper">
            {fieldNameComponent(field, fields, setFields, setIsSaveDisabled)}
          </div>
          {isUuid(field.id) ? (
            <div className="delete-warpper">
              <span
                onClick={confirmDelete}
                className="appkiticon icon-circle-delete-fill"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SortableList = (props) => {
  const { fields, onSortEnd } = props;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 20, // This replaces sortableContainerDefaultProps.distance
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over.id);

      onSortEnd({ oldIndex, newIndex });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]} // This replaces axis="y" and lockAxis="y"
    >
      <SortableContext
        items={fields.map((field) => field.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul>
          {fields.map((field, index) => {
            if (
              !Array.from(ParentAndChildItemMap.values())
                .reduce(
                  (accumulator, currentValue) => [
                    ...accumulator,
                    ...currentValue,
                  ],
                  [],
                )
                .includes(field.name)
            ) {
              return (
                <SortableItem
                  key={`item-${field.id}`}
                  field={field}
                  {...props}
                />
              );
            }
            return null;
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

const CombinedForm = (props) => <SortableList {...props} />;

const getFields = (fields) =>
  fields.map((f) => ({
    id: f.info.resourceId ? f.info.resourceId : f.info.value,
    name: f.info.value,
    isHidden: f.info.hideFlag,
  }));

const freshOrderPorp = (fields) =>
  fields.map((e, i) => {
    e.order = i + 1;
    return e;
  });

const FieldSettings = ({
  selectedMenu,
  fields: _fields,
  refetch,
  childrenFields,
  setSettingOpen,
}) => {
  const isInternal = JSON.parse(
    window.localStorage.userInfo || '{}',
  ).isInternal;
  const [fields, setFields] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const projectId = useSelector((state) => state.common.currentId);

  useEffect(() => {
    setIsSaveDisabled(
      fields.some((field) => field.isRequiredError || field.isDuplicatedError),
    );
  }, [fields]);

  const handleAddRow = () => {
    setFields([
      ...fields,
      {
        id: uuidv4(),
        name: '',
        isHidden: false,
        isRequiredError: true,
        order: fields.length + 1,
      },
    ]);
    setIsSaveDisabled(true);
  };

  const getTargetIndex = (newFields, targetName) => {
    let result;
    newFields.forEach((f, i) => {
      if (f.name === targetName) {
        result = i;
      }
    });
    return result;
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    let newFields = dndArrayMove(fields, oldIndex, newIndex);

    if (
      Array.from(ParentAndChildItemMap.keys()).includes(fields[oldIndex].name)
    ) {
      if (oldIndex > newIndex) {
        ParentAndChildItemMap.get(fields[oldIndex].name).map((child, i) => {
          newFields = dndArrayMove(
            newFields,
            getTargetIndex(newFields, child),
            getTargetIndex(newFields, fields[oldIndex].name) + i + 1,
          );
        });
      }

      if (oldIndex < newIndex) {
        ParentAndChildItemMap.get(fields[oldIndex].name).map((child, i) => {
          newFields = dndArrayMove(
            newFields,
            getTargetIndex(newFields, child),
            getTargetIndex(newFields, fields[oldIndex].name) + i,
          );
        });
      }
    }
    setFields(freshOrderPorp(newFields));
  };

  const resetFields = useCallback(() => {
    const defaultFields = getFields(_fields);
    setFields(freshOrderPorp(defaultFields));
  }, [_fields]);

  const onCancel = useCallback(() => {
    setModalVisible(false);
    setSettingOpen(false);
    setIsSaveDisabled(false);
    resetFields();
  }, [resetFields]);

  const onCheckboxChange = (name) => (e) =>
    setFields((prevFields) => {
      const prevField = prevFields.find((f) => f.name === name);
      prevField.isHidden = !e.target.checked;

      if (Array.from(ParentAndChildItemMap.keys()).includes(name)) {
        ParentAndChildItemMap.get(name).forEach((childName) => {
          const childrenField = prevFields.find((f) => f.name === childName);
          childrenField.isHidden = !e.target.checked;
        });
      }

      return [...prevFields];
    });

  const onSave = async () => {
    setIsSaveLoading(true);
    await API.updateCategories(projectId, selectedMenu, fields);
    setIsSaveLoading(false);
    setModalVisible(false);
    setSettingOpen(false);
    refetch();
  };

  useEffect(() => {
    resetFields();
  }, [resetFields]);

  if (
    !isInternal ||
    ![
      SEGMENTS.companyoverview,
      SEGMENTS.financials,
      SEGMENTS.gotomarket,
      SEGMENTS.operationalfootprint,
    ].includes(selectedMenu)
  ) {
    return null;
  }

  return (
    <div className="analysis-fields-settings role-control">
      <Button
        onClick={() => {
          setSettingOpen(true);
          setModalVisible(true);
        }}
        style={{ zIndex: 1 }}
        kind="secondary"
      >
        Customize Fields
      </Button>
      <Modal
        className="fields-setting-modal"
        visible={modalVisible}
        onOk={onSave}
        okButtonProps={{
          disabled: isSaveDisabled,
        }}
        onCancel={onCancel}
        closeIcon={<span className="appkiticon icon-close- fill" />}
        okText="save"
        cancelText="cancel"
        confirmLoading={isSaveLoading}
        destroyOnClose
        maskClosable={false}
      >
        <h3 className="title">Customize Fields</h3>
        <div className="add-new-button">
          <a onClick={handleAddRow}>
            <span>+</span>
            <span>Add New</span>
          </a>
        </div>

        <div className="draggable-table-container">
          <div className="sort-content">
            <CombinedForm
              fields={fields}
              onSortEnd={onSortEnd}
              onCheckboxChange={onCheckboxChange}
              setFields={setFields}
              setIsSaveDisabled={setIsSaveDisabled}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FieldSettings;
